import type { ActivityDriver, ActivityEntry, ActivityShift, ActivityTotals } from "./activity-types";

/** Small facts about entries and totals that building, filtering, and summarizing all share. */
export const emptyTotals = (): ActivityTotals => ({ shifts: 0, loads: 0, quantity: 0, revenue: 0, cost: 0, mileage: 0, attention: 0, workedMillis: 0 });

/** A load or a one-sided ticket, as opposed to a site visit or a dispatch. */
export const isActivityTicket = (entry: ActivityEntry) => entry.kind === "load" || entry.kind === "pickup" || entry.kind === "delivery";

export const entryQuantity = (entry: ActivityEntry) => entry.pickup?.quantityDecimal ?? entry.delivery?.quantityDecimal ?? 0;

export const totalsFor = (shifts: Pick<ActivityShift, "shift" | "entries">[], now: number): ActivityTotals => {
  const totals = emptyTotals();
  for (const { shift, entries } of shifts) {
    if (shift) {
      totals.shifts++;
      totals.workedMillis += Math.max((shift.endDttm ?? now) - shift.startDttm, 0);
    }
    for (const entry of entries) {
      if (!isActivityTicket(entry)) continue;
      totals.loads++;
      totals.quantity += entryQuantity(entry);
      totals.quantityUom ??= entry.pickup?.quantityUnitOfMeasure ?? entry.delivery?.quantityUnitOfMeasure;
      totals.revenue += entry.revenue ?? 0;
      totals.cost += entry.cost ?? 0;
      totals.mileage += entry.tta?.mileage ?? 0;
      if (entry.needsAttention) totals.attention++;
    }
  }
  return totals;
};

export const withTotals = (driver: ActivityDriver, shifts: ActivityShift[], now: number): ActivityDriver => ({
  ...driver,
  shifts: shifts.map((shift) => ({ ...shift, totals: totalsFor([shift], now) })),
  totals: totalsFor(shifts, now),
});

/** Key of the pseudo-driver that holds tank level drops, which belong to no one. */
export const TANK_DROPS_KEY = "tank-drops";

export const isUnvalidated = (entry: ActivityEntry) => entry.kind === "load" && entry.validation.some((step) => step.validatedDttm == null);

export const flattenEntries = (drivers: ActivityDriver[]): ActivityEntry[] =>
  drivers.flatMap((driver) => driver.shifts.flatMap((shift) => shift.entries));
